import React from 'react';
import Card from 'react-bootstrap/Card';
import moment from 'moment';
import { getStateIcon } from '../../../util/jobUtils';
import dateService from '../../../services/dateService';
import { isMultiLocation, isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import { getTechnicianColor, getWashFromAccent } from '../../../util/colorCodingUtils';
import { isMarineVehicle } from '../../../util/assetType';

const STATE_LABELS = {
  new: 'New',
  answered: 'Answered',
  scheduled: 'Scheduled',
  received: 'Received',
  started: 'In Progress',
  finished: 'Finished',
  paid: 'Paid',
  collected: 'Collected'
};

const PRIORITY_BADGE = {
  urgent: 'jv-badge jv-badge-pill jv-badge-danger',
  high: 'jv-badge jv-badge-pill jv-badge-warning',
  normal: 'jv-badge jv-badge-pill jv-badge-info',
  low: 'jv-badge jv-badge-pill jv-badge-neutral'
};

const TaskCard = ({ item, onItemClick, locations, selectedLocationId }) => {
  const badgeCls = PRIORITY_BADGE[item.priority] || PRIORITY_BADGE.normal;
  const localDue = item.due_date ? dateService.fromAPIDate(item.due_date) : null;
  const isOverdue = localDue && localDue.isBefore(moment(), 'day');
  const multiLoc = isMultiLocation(locations);
  const allLocView = isAllLocations(selectedLocationId);
  const loc = multiLoc && allLocView && item.location_id
    ? (locations || []).find(l => String(l.location_id) === String(item.location_id))
    : null;
  return (
    <div
      key={item.requestId}
      className="today-job-item installer"
      style={{ borderLeftColor: 'var(--color-purple)' }}
      onClick={() => onItemClick(item)}
    >
      <div className="today-job-info">
        <div className="today-job-service">{item.title}</div>
        <div className="today-job-customer">
          {localDue && (
            <span style={isOverdue ? { color: 'var(--color-danger)', fontWeight: 600 } : {}}>
              {isOverdue ? 'Overdue: ' : 'Due: '}
              {localDue.format('MMM D')}
            </span>
          )}
          {item.assigned_to_name && (
            <span className="today-job-tech">
              <i className="fa-sharp fa-regular fa-user" /> {item.assigned_to_name}
            </span>
          )}
          {loc && (
            <span className="jv-location-badge" style={{ backgroundColor: getLocationBadgeColor(item.location_id, locations) }}>
              {getLocationName(loc)}
            </span>
          )}
        </div>
      </div>
      <div className="today-job-state">
        <span className={badgeCls}>
          {item.priority || 'normal'}
        </span>
      </div>
    </div>
  );
};

const DayViewSection = ({ title, icon, color, items, userType, onItemClick, emptyMessage, locations, selectedLocationId, technicians }) => {
  return (
    <Card className="today-section-card">
      <Card.Header
        className="today-section-header"
        style={{ borderLeftColor: color }}
      >
        <div className="today-section-title">
          <i className={`${icon} today-section-icon`} style={{ color }} />
          <span>{title}</span>
          <span className="today-section-count" style={{ backgroundColor: color }}>
            {items.length}
          </span>
        </div>
      </Card.Header>

      <Card.Body className="today-section-body">
        {items.length === 0 ? (
          <div className="today-section-empty">{emptyMessage || 'Nothing here'}</div>
        ) : (
          <div className="today-job-list">
            {items.map((item) => {
              if (item._isTask) {
                return <TaskCard key={item.requestId} item={item} onItemClick={onItemClick} locations={locations} selectedLocationId={selectedLocationId} />;
              }
              const stateIconCls = getStateIcon(item.currentState, userType);
              const isDealer = item.requestType === 'dealer';
              // Use server-provided color directly, fall back to staff list lookup
              const directColor = item.assignedTechnicianColor;
              const techColor = directColor
                ? { wash: getWashFromAccent(directColor), accent: directColor }
                : getTechnicianColor(item.assigned_technician_id || item.assignedTechnicianId, technicians);
              const cardStyle = techColor
                ? { background: techColor.wash, borderLeftColor: techColor.accent }
                : {};
              return (
                <div
                  key={item.requestId}
                  className={`today-job-item ${isDealer ? 'dealer' : 'installer'}`}
                  style={cardStyle}
                  onClick={() => onItemClick(item)}
                >
                  <div className="today-job-info">
                    <div className="today-job-service">{item.serviceRequested}</div>
                    <div className="today-job-vehicle">
                      {isMarineVehicle(item) && (
                        <i className="fa-sharp fa-regular fa-sailboat mr-1" title="Boat" />
                      )}
                      {item.vehicleColor && `${item.vehicleColor} `}
                      {item.vehicleYear} {item.vehicleMake} {item.vehicleModel}
                    </div>
                    <div className="today-job-customer">
                      {item.dealerName}
                      {item.assignedTechnicianName && (
                        <span className="today-job-tech">
                          <i className="fa-sharp fa-regular fa-user" /> {item.assignedTechnicianName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="today-job-state">
                    {stateIconCls && <i className={stateIconCls} />}
                    <span className="today-job-state-label">{STATE_LABELS[item.currentState] || item.currentState}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DayViewSection;
